import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, AlarmClock, ChevronDown, Calendar, Users, User } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { stripMentionIds } from "../../lib/constants";
import { EXIT_MS, wait, snoozeOptions, shortWhen, shortTitle, dueShort } from "../../lib/taskActions";
import DeadlinePicker from "../DeadlinePicker";

const PRIORITY_BAR = { urgent: "bg-red-500", high: "bg-amber-500", medium: "bg-blue-400", low: "bg-slate-300" };
const TONE_TEXT = { red: "text-red-600", amber: "text-amber-700", slate: "text-slate-500" };

// Compact task rows with Done + Snooze right on the dashboard. Rows slide
// out on success; onRemoved(id) drops them from the parent list and
// onChanged() lets the parent refresh counts.
export default function QuickTaskList({ tasks, onRemoved, onChanged, empty }) {
  const nav = useNavigate();
  const [exiting, setExiting] = useState({});
  const [menuFor, setMenuFor] = useState(null);
  const [pickerFor, setPickerFor] = useState(null);

  const run = async (t, kind, patch, toastText, before) => {
    setMenuFor(null);
    setExiting((c) => ({ ...c, [t.id]: kind }));
    try {
      await Promise.all([api.patch(`/tasks/${t.id}`, patch), wait(EXIT_MS)]);
      onRemoved(t.id);
      onChanged?.();
      toast.success(toastText, {
        action: { label: "Undo", onClick: async () => { await api.patch(`/tasks/${t.id}`, before); onChanged?.(true); } },
        duration: 6000,
      });
    } catch {
      toast.error("Save failed");
    }
    setExiting((c) => { const n = { ...c }; delete n[t.id]; return n; });
  };

  const done = (t) => run(t, "done", { status: "completed" }, `Done: ${shortTitle(t)}`, { status: t.status });
  const snooze = (t, date) => run(t, "snoozed", { deadline: date.toISOString() }, `Moved to ${shortWhen(date)}`, { deadline: t.deadline || null });

  if (!tasks.length) return empty;

  return (
    <div>
      {tasks.map((t, i) => {
        const ex = exiting[t.id];
        const due = dueShort(t.deadline);
        const from = t.chat_type === "group"
          ? `${stripMentionIds(t.chat_name || "")} › ${t.sender_name || ""}`
          : (t.sender_name || t.chat_name || "");
        return (
          <div key={t.id} className={`sw-collapse relative border-b last:border-b-0 border-slate-100 ${ex ? "is-collapsed" : ""} ${menuFor === t.id ? "z-30" : ""}`}>
            <div>
              <div
                className={"group relative flex items-center gap-3 pl-0 pr-3 py-2.5 bg-white hover:bg-slate-50/80 cursor-pointer transition-colors " +
                  (ex ? "sw-exit-approve pointer-events-none" : "sw-fade-up-clean")}
                style={ex ? undefined : { animationDelay: `${i * 30}ms` }}
                onClick={() => nav(`/app/tasks?focus=${t.id}`)}
                data-testid={`quick-task-${t.id}`}
              >
                {ex && (
                  <div className={`absolute inset-0 z-10 grid place-items-center sw-fade-in ${ex === "done" ? "bg-emerald-50/85" : "bg-amber-50/85"}`}>
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[12px] font-semibold text-white shadow-sm sw-pop ${ex === "done" ? "bg-emerald-600" : "bg-amber-600"}`}>
                      {ex === "done" ? <><CheckCircle2 size={13} /> Done</> : <><AlarmClock size={13} /> Snoozed</>}
                    </span>
                  </div>
                )}
                <div className={"w-1 self-stretch rounded-r " + (PRIORITY_BAR[t.priority] || "bg-slate-200")} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 min-w-0">
                    {t.task_no && <span className="font-mono text-[10.5px] text-slate-400 shrink-0">#{t.task_no}</span>}
                    <span className="text-[13.5px] font-semibold text-slate-900 truncate">{t.title}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5 text-[11.5px] min-w-0">
                    <span className={"font-semibold shrink-0 " + TONE_TEXT[due.tone]}>{due.text}</span>
                    {from && (
                      <span className="inline-flex items-center gap-1 text-slate-500 truncate">
                        {t.chat_type === "group" ? <Users size={11} className="shrink-0 text-slate-400" /> : <User size={11} className="shrink-0 text-slate-400" />}
                        <span className="truncate">{from}</span>
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0" onClick={(e) => e.stopPropagation()}>
                  <button onClick={() => done(t)} title="Mark done"
                    className="h-8 px-2.5 inline-flex items-center gap-1 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[12px] font-semibold transition active:scale-95"
                    data-testid={`quick-done-${t.id}`}>
                    <CheckCircle2 size={14} /> <span className="hidden sm:inline">Done</span>
                  </button>
                  <div className="relative">
                    <button onClick={() => setMenuFor(menuFor === t.id ? null : t.id)} title="Snooze"
                      className={"h-8 px-2 inline-flex items-center gap-0.5 rounded-lg border bg-white text-slate-600 hover:text-amber-700 hover:border-amber-400 transition " + (menuFor === t.id ? "border-amber-400 text-amber-700" : "border-slate-200")}
                      data-testid={`quick-snooze-${t.id}`}>
                      <AlarmClock size={14} /> <ChevronDown size={11} className="text-slate-400" />
                    </button>
                    {menuFor === t.id && (
                      <>
                        <div className="fixed inset-0 z-20" onClick={() => setMenuFor(null)} />
                        <div className="absolute right-0 top-9 z-30 w-56 bg-white border border-slate-200 rounded-xl shadow-xl py-1.5 sw-pop">
                          {snoozeOptions().map((o) => (
                            <button key={o.key} onClick={() => snooze(t, o.date)}
                              className="w-full flex items-center gap-2 px-3 py-1.5 text-left text-[12.5px] text-slate-800 hover:bg-slate-50">
                              <span className="flex-1">{o.label}</span>
                              <span className="text-[11px] text-slate-400 tabular-nums">{shortWhen(o.date)}</span>
                            </button>
                          ))}
                          <div className="h-px bg-slate-100 my-1" />
                          <button onClick={() => { setMenuFor(null); setPickerFor(t); }}
                            className="w-full flex items-center gap-2 px-3 py-1.5 text-left text-[12.5px] text-slate-800 hover:bg-slate-50">
                            <Calendar size={13} className="text-slate-400" /> Pick date & time…
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}

      {pickerFor && (
        <DeadlinePicker
          taskId={pickerFor.id}
          currentDeadline={pickerFor.deadline || null}
          onCancel={() => setPickerFor(null)}
          onSelect={(deadline) => {
            const t = pickerFor;
            setPickerFor(null);
            if (deadline) snooze(t, new Date(deadline));
          }}
        />
      )}
    </div>
  );
}
